import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUpRightFromSquare } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { useOsActionsOptional } from "@/components/ui/os-layout/OsContext";
import type { ContentNode } from "./contentNode";
import { ReadableContent } from "./ReadableContent";
import { openReadableContent } from "./openReadableContent";

export interface ReadableDataPanelProps {
  title: string;
  content: ContentNode | null | undefined;
  className?: string;
  /** Tailwind max-height utility for the inline scroll area. */
  maxHeightClass?: string;
  "data-testid"?: string;
}

/**
 * Scrollable block for audit context (structured content tree).
 * Pop-out control floats over the top-right corner of the text.
 */
export function ReadableDataPanel({
  title,
  content,
  className,
  maxHeightClass = "max-h-96",
  "data-testid": testId = "readable-data-panel",
}: ReadableDataPanelProps) {
  const osActions = useOsActionsOptional();

  if (!content) {
    return (
      <p className="text-sm text-slate-500" data-testid={`${testId}-empty`}>
        No content.
      </p>
    );
  }

  return (
    <div
      className={clsx(
        "relative overflow-hidden rounded-lg border border-white/10 bg-slate-900/40",
        className
      )}
      data-testid={testId}
      data-has-content="true"
    >
      <button
        type="button"
        onClick={() =>
          openReadableContent({ title, content }, osActions?.openWindow ?? null)
        }
        className="absolute right-1.5 top-1.5 z-10 inline-flex h-6 w-6 items-center justify-center rounded-md bg-slate-900/80 text-slate-400 ring-1 ring-white/10 hover:bg-slate-800 hover:text-slate-200"
        aria-label={`Open ${title} in a new window`}
        title="Open in window"
        data-testid={`${testId}-popout`}
      >
        <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="text-[11px]" />
      </button>
      <div className={clsx("overflow-auto p-2.5 pr-8", maxHeightClass)}>
        <ReadableContent node={content} variant="panel" />
      </div>
    </div>
  );
}
